/**
 * @file TranslationUnavailableNoticeContainer.tsx
 * @description Resolves the notice's copy from the i18n catalogs (SE-6510).
 * This directory sits inside the SE-6507 zero-literals scope, so every string
 * it renders comes from a catalog rather than a literal.
 * @module reports
 */

import React from "react";
import { useTranslation } from "react-i18next";

import TranslationUnavailableNoticeView from "./TranslationUnavailableNoticeView";

/**
 * Props for the TranslationUnavailableNotice container
 */
interface Props {
  /** Called when the viewer asks to try the translation again */
  onRetry: () => void;
  /** Whether a retry is already in flight */
  isRetrying?: boolean;
}

/**
 * Container for the translation-unavailable notice.
 *
 * The report body it sits above is, by definition, the ORIGINAL text — a
 * translation failure is the only way this renders — so the notice being in
 * the app's UI locale rather than the report's is coherent rather than the
 * mismatch SE-4579 fixed for the "Matches:" heading.
 * @param root0 - Component props
 * @param root0.onRetry - Called when the viewer asks to try again
 * @param root0.isRetrying - Whether a retry is already in flight
 */
const TranslationUnavailableNoticeContainer = ({
  onRetry,
  isRetrying,
}: Props) => {
  const { t } = useTranslation();

  return (
    <TranslationUnavailableNoticeView
      message={t("reports.translationUnavailable")}
      retryLabel={t("reports.retryTranslation")}
      onRetry={onRetry}
      isRetrying={!!isRetrying}
    />
  );
};

TranslationUnavailableNoticeContainer.displayName =
  "TranslationUnavailableNoticeContainer";

export default TranslationUnavailableNoticeContainer;
